import {
  Button,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  RoundedModalContent,
  RoundedModalFooter,
  RoundedModalHeader,
  RoundedModalTitle,
  Text,
} from '@bridebook/aura';
import { IGuest } from '@bridebook/models/source/models/Weddings/Guests.types';
import { InputV2 } from '@bridebook/ui';
import { MainGuestEmailButton } from '@bridebook/web/components/guestlist/main-guest-email/main-guest-email-button';
import { useMainGuestEmail } from '@bridebook/web/components/guestlist/main-guest-email/use-main-guest-email';
import { useUpdateGuestEmail } from '@bridebook/web/lib/guestlist/hooks/query/use-update-guest-email';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'next-i18next';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

interface IProps {
  guest: IGuest;
  headGuest?: IGuest;
  onEmailUpdated?: () => void;
  variant?: 'modal' | 'drawer';
}

export const UpdateEmailForm = ({
  guest,
  headGuest,
  onEmailUpdated,
  variant = 'modal',
}: IProps) => {
  const { t } = useTranslation('inbox');
  const defaultEmailValue = guest?.contacts?.email || '';

  const formSchema = z.object({
    email: z.string().email(t('guests.createMessageFlow.updateEmailForm.emailError')),
  });

  type FormValues = z.infer<typeof formSchema>;

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isValid },
  } = useForm<FormValues>({
    defaultValues: {
      email: defaultEmailValue,
    },
    resolver: zodResolver(formSchema),
    mode: 'onTouched',
  });

  const { mutateAsync: updateGuestEmail, isLoading } = useUpdateGuestEmail();

  const onSubmit = async (data: FormValues) => {
    await updateGuestEmail({ guestId: guest.id, email: data.email });
    onEmailUpdated?.();
  };

  const currentEmail = watch('email');

  const {
    isVisible: showUseHeadGuestEmail,
    isUsingHeadGuestEmail,
    headGuestEmail,
  } = useMainGuestEmail({
    guest,
    headGuest,
    currentEmail,
  });

  const title = t('guests.createMessageFlow.updateEmailForm.title');

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)} id="update-email-form" className="flex flex-col gap-4">
      <Text variant="typo-base" className="text-center text-space">
        {guest.name}
      </Text>
      <div className="flex flex-col gap-2 text-left">
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
            <InputV2
              label={t('guests.createMessageFlow.updateEmailForm.emailLabel')}
              name="email"
              id="email"
              type="email"
              placeholder={t('guests.createMessageFlow.updateEmailForm.emailPlaceholder')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={error?.message}
            />
          )}
        />
        {showUseHeadGuestEmail && (
          <MainGuestEmailButton
            className="self-start"
            isUsingHeadGuestEmail={isUsingHeadGuestEmail}
            onUseEmail={() => setValue('email', headGuestEmail, { shouldValidate: true })}
          />
        )}
      </div>
    </form>
  );

  const submitButton = (
    <Button
      type="submit"
      form="update-email-form"
      disabled={!isValid || isLoading}
      loading={isLoading}
      className="min-w-[140px]">
      {t('common:button.save')}
    </Button>
  );

  if (variant === 'drawer') {
    return (
      <>
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
        </DrawerHeader>
        <DrawerContent>{formContent}</DrawerContent>
        <DrawerFooter>{submitButton}</DrawerFooter>
      </>
    );
  }

  return (
    <>
      <RoundedModalHeader>
        <RoundedModalTitle>{title}</RoundedModalTitle>
      </RoundedModalHeader>
      <RoundedModalContent>{formContent}</RoundedModalContent>
      <RoundedModalFooter>{submitButton}</RoundedModalFooter>
    </>
  );
};
